/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  alpha,
  createTheme,
  darken,
  lighten,
  type PaletteOptions,
  SvgIcon,
  type SvgIconProps,
  type SxProps,
  type TextFieldProps,
  type Theme,
  useTheme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import type { AccentPreference, SimulationService } from '@poweralpha/common';
import { get } from 'lodash';
import { createElement } from 'react';
import { getGhostButtonSx } from './desktop/ghostButton';
import { APP_FONT_FAMILY } from './fonts';
import type {} from './theme/augmentation';
import {
  accents,
  type ColorMode,
  component,
  type DomainColor,
  domain,
  external,
  getThemeTokens,
  semantic,
} from './theme/tokens';

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    tiny: true;
    xs: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    tiny: true;
    xs: true;
  }
}

/** Shared asset colors used by home icons and listing badges. */
export const TECHNOLOGY_COLORS = {
  pv: '#F5C542',
  bess: '#4EA8F5',
} as const;

export type ThemeColors = Record<'dark' | 'light', string>;

const checkboxViewBox = '0 0 20 20';

export const CheckboxUncheckedIcon = (props: SvgIconProps) =>
  createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
  );

export const CheckboxCheckedIcon = (props: SvgIconProps) => {
  const theme = useTheme();
  // The tick sits on the primary fill, so it uses the primary's contrast colour.
  const markColor = theme.palette.primary.contrastText;

  return createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'currentColor',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
    createElement('path', {
      d: 'M6.1 10.2L8.65 12.75L13.9 7.5',
      fill: 'none',
      stroke: markColor,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth: 2.1,
    }),
  );
};

export const CheckboxIndeterminateIcon = (props: SvgIconProps) => {
  const theme = useTheme();
  // The tick sits on the primary fill, so it uses the primary's contrast colour.
  const markColor = theme.palette.primary.contrastText;

  return createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'currentColor',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
    createElement('rect', {
      x: 5.5,
      y: 9,
      width: 9,
      height: 2,
      rx: 1,
      fill: markColor,
    }),
  );
};

/** MUI palette for one mode, built from the v4 tokens (`theme/tokens.ts`, DESIGN.md). */
const buildPalette = (mode: ColorMode): PaletteOptions => {
  const tokens = semantic[mode];
  return {
    primary: accents.sky[mode],
    secondary: tokens.secondary,
    info: tokens.info,
    success: tokens.success,
    warning: tokens.warning,
    error: tokens.error,
    background: tokens.background,
    text: tokens.text,
    divider: tokens.border.default,
    border: tokens.border,
    overlay: tokens.overlay,
    shadow: tokens.shadow,
    dataviz: tokens.dataviz,
    component: component[mode],
    domain: domain[mode],
  };
};

export const lightTheme: PaletteOptions = buildPalette('light');

export const darkTheme: PaletteOptions = buildPalette('dark');

// Reserved for content written by the PA Agent (design: agentic bid request prototype).
export const getAgentColor = (theme: Theme) => getThemeTokens(theme).component.agent.foreground;

export const getAgentBackgroundColor = (theme: Theme) =>
  getThemeTokens(theme).component.agent.background;

export const getSuperAdminBadgeColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.info.main, 0.06)
    : darken(theme.palette.info.dark, 0.18);

export const getSuperAdminBadgeBackgroundColor = (theme: Theme) =>
  alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.2 : 0.12);

type CommonColors = 'secondary' | 'primary' | 'info' | 'warning' | 'error';

export const highlightBackground = (
  theme: Theme, // FIXME: refactor to Theme['palette']
  opts?: {
    color?: string | CommonColors | Record<'dark' | 'light', string>;
    coefficient?: number | Record<'dark' | 'light', number>;
    reverse?: boolean;
  },
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: fine
) => {
  let color: string;
  if (opts?.color) {
    if (typeof opts.color === 'object' && 'dark' in opts.color && 'light' in opts.color) {
      color = opts.color[theme.palette.mode];
    } else if (opts.color in theme.palette) {
      if (opts.color === 'divider') {
        color = theme.palette.divider;
      } else {
        color = theme.palette[opts.color as CommonColors].main;
      }
    } else if (opts.color.includes('.')) {
      color = get(theme.palette, opts.color);
    } else {
      color = opts.color;
    }
  } else {
    color = theme.palette.background.paper;
  }

  const coefficient = opts?.coefficient ?? 0.1;
  const coeff = typeof coefficient === 'object' ? coefficient[theme.palette.mode] : coefficient;
  if (opts?.reverse) {
    return theme.palette.mode === 'dark' ? darken(color, coeff) : lighten(color, coeff);
  }
  return theme.palette.mode === 'dark' ? lighten(color, coeff) : darken(color, coeff);
};

export const blue = {
  100: '#DAECFF',
  200: '#b6daff',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  900: '#003A75',
};

export const grey = {
  50: '#f6f8fa',
  100: '#eaeef2',
  200: '#d0d7de',
  300: '#afb8c1',
  400: '#8c959f',
  500: '#6e7781',
  600: '#57606a',
  700: '#424a53',
  800: '#32383f',
  900: '#24292f',
};

export const errorPalette: Record<'light' | 'dark', string> = {
  dark: semantic.dark.text.error,
  light: semantic.light.text.error,
};

export const scrollBarColors = {
  light: {
    thumb: component.light.scrollbar.thumb,
    activeThumb: component.light.scrollbar.thumbActive,
  },
  dark: {
    thumb: component.dark.scrollbar.thumb,
    activeThumb: component.dark.scrollbar.thumbActive,
  },
};

const scrollBarStyleOverrides = (mode: 'dark' | 'light') => {
  const { thumb, activeThumb } = scrollBarColors[mode];
  const track = 'transparent';
  return {
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      backgroundColor: `${thumb}`,
      minHeight: 24,
      border: `3px solid ${track}`,
      backgroundClip: 'content-box',
    },
    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },
  };
};

export const textFieldBorderRadius = 10;
export const buttonBorderRadius = 10;
export const toggleButtonBorderRadius = 10;
export const alertBorderRadius = 12;
// Matches an `sx` borderRadius of 4 (4 x the default 4px shape radius).
export const popoverBorderRadius = 16;

export const getInputSurfaceColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.background.paper, 0.04)
    : darken(theme.palette.background.paper, 0.02);

/** Raised card surface: the raised tone in dark mode, the plain paper in light mode. */
export const getRaisedSurfaceColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? getThemeTokens(theme).background.raised
    : theme.palette.background.paper;

export const getCompactTextFieldClassName = (size: TextFieldProps['size']) =>
  size === 'tiny' || size === 'xs' ? `pa-textfield-size-${size}` : undefined;

const tinyTextFieldSx = {
  '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    marginTop: '1px',
    height: 25,
    pr: 0,
    fontSize: 14,
    borderRadius: 8,
  },
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    pl: 1,
  },
  '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: 14,
  },
  '& .MuiPickersInputBase-sectionContent': {
    fontSize: 14,
    lineHeight: 1,
  },
  '& .MuiPickersInputBase-root .MuiInputAdornment-positionEnd, & .MuiPickersOutlinedInput-root .MuiInputAdornment-positionEnd':
    {
      marginLeft: 2,
      marginRight: -4,
    },
  '& .MuiPickersInputBase-root [data-mui-picker-open-button="true"], & .MuiPickersOutlinedInput-root [data-mui-picker-open-button="true"]':
    {
      padding: 4,
    },
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    height: 'auto',
    minHeight: 25,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    fontSize: 16,
  },
  '& .MuiInputLabel-root': {
    fontSize: 16,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend, & .MuiPickersOutlinedInput-notchedOutline legend': {
    fontSize: 12,
  },
};

const xsTextFieldSx = {
  // An empty field's resting label would run into its unit; stop it short of the unit.
  '&:has(.MuiInputAdornment-positionEnd) .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    maxWidth: 'calc(100% - 80px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    height: 30,
    pr: 0,
  },
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    pl: 1,
  },
  '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  '& .MuiPickersInputBase-sectionContent': {
    lineHeight: 1,
  },
  '& .MuiPickersInputBase-root .MuiInputAdornment-positionEnd, & .MuiPickersOutlinedInput-root .MuiInputAdornment-positionEnd':
    {
      marginLeft: 2,
      marginRight: -4,
    },
  '& .MuiPickersInputBase-root [data-mui-picker-open-button="true"], & .MuiPickersOutlinedInput-root [data-mui-picker-open-button="true"]':
    {
      padding: 4,
    },
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    height: 'auto',
    minHeight: 30,
  },
  '& .MuiInputLabel-root': {
    fontSize: 15,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend, & .MuiPickersOutlinedInput-notchedOutline legend': {
    fontSize: 11.3,
  },
};

// MUI's small field keeps the 16px body text and a 40px box; the v4 toolbars and pickers want a
// compact control whose floating label matches its notch.
const smallTextFieldSx = {
  '& .MuiInputBase-sizeSmall': { fontSize: 13.5 },
  '& .MuiInputBase-sizeSmall:not(.MuiInputBase-multiline):not(.MuiAutocomplete-inputRoot)': {
    height: 34,
  },
  '& .MuiInputLabel-sizeSmall': {
    fontSize: 13.5,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(12px, 8px) scale(1)' },
    '&.MuiInputLabel-shrink': { transform: 'translate(14px, -7px) scale(0.85)' },
  },
  '& .MuiInputBase-sizeSmall .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 11.5,
  },
  '& .MuiInputBase-sizeSmall .MuiSelect-icon': { fontSize: 20, right: 6 },
};

export const getTextFieldSizeSx = (size: TextFieldProps['size']) => {
  if (size === 'tiny') {
    return tinyTextFieldSx;
  }
  if (size === 'xs') {
    return xsTextFieldSx;
  }
  if (size === 'small') {
    // xs and tiny fields render as MUI `small` too; their own class sets their size.
    return { '&:not(.pa-textfield-size-xs):not(.pa-textfield-size-tiny)': smallTextFieldSx };
  }
  return undefined;
};

/** User-selectable primary colors (Settings › Appearance › Accent color); `sky` is the default. */
export const getAccentMainColor = (accent: AccentPreference, themeMode: 'dark' | 'light') =>
  accents[accent][themeMode].main;

/** Every MUI X chart (Pro and Premium included) colors its series from categorical slots 1-8. */
const chartDefaults = (mode: ColorMode) => ({
  MuiChartsDataProvider: {
    defaultProps: { colors: semantic[mode].dataviz.categorical.slice(0, 8) },
  },
});

export const getTheme = (themeMode: 'dark' | 'light', accent: AccentPreference = 'sky') =>
  createTheme({
    palette: {
      mode: themeMode,
      ...(themeMode === 'dark' ? darkTheme : lightTheme),
      primary: accents[accent][themeMode],
    },
    typography: {
      fontFamily: APP_FONT_FAMILY,
      h1: { fontSize: '2.25rem' },
      h2: { fontSize: '2rem' },
      h3: { fontSize: '1.75rem' },
      h4: { fontSize: '1.5rem' },
      h5: { fontSize: '1.25rem' },
      h6: { fontSize: '1.1rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: scrollBarStyleOverrides(themeMode),
        },
      },
      // Spread because @mui/x-charts-premium ships no theme augmentation for this key.
      ...chartDefaults(themeMode),
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const severity = ownerState.severity ?? 'success';
            const palette = theme.palette[severity];
            const backgroundAlpha = theme.palette.mode === 'dark' ? 0.24 : 0.16;
            const borderAlpha = theme.palette.mode === 'dark' ? 0.55 : 0.35;

            return {
              borderRadius: alertBorderRadius,
              padding: '0px 16px 0px',
              ...(ownerState.variant === 'standard' && {
                backgroundColor: alpha(palette.main, backgroundAlpha),
                border: `1px solid ${alpha(palette.main, borderAlpha)}`,
                color: theme.palette.text.primary,
                '& .MuiAlert-icon': {
                  color:
                    theme.palette.mode === 'dark' && severity !== 'warning'
                      ? palette.light
                      : palette.main,
                },
              }),
              ...(ownerState.variant === 'outlined' && {
                borderColor: alpha(palette.main, 0.7),
              }),
            };
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: buttonBorderRadius,
            '& .MuiButton-startIcon': {
              marginRight: 4,
            },
            '& .MuiButton-endIcon': {
              marginLeft: 4,
            },
            variants: [
              {
                // The v4 mockups' ghost pill (`.btn.ghost`): a soft glass fill instead of a
                // coloured outline. Coloured outlines (error, success, ...) keep their look.
                props: { variant: 'outlined', color: 'primary' },
                style: getGhostButtonSx(theme),
              },
              {
                props: {
                  size: 'tiny',
                  variant: 'text',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'text',
                },
                style: {
                  height: 30,
                },
              },
              {
                props: {
                  size: 'tiny',
                  variant: 'outlined',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'outlined',
                },
                style: {
                  height: 30,
                },
              },
              {
                props: {
                  size: 'tiny',
                  variant: 'contained',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'contained',
                },
                style: {
                  height: 30,
                },
              },
            ],
          }),
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: buttonBorderRadius,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => {
            const isDark = theme.palette.mode === 'dark';

            return {
              backgroundColor: isDark
                ? theme.palette.background.raised
                : theme.palette.background.paper,
              backgroundImage: 'none',
              borderRadius: 16,
              color: theme.palette.text.primary,
              border: `1px solid ${
                isDark
                  ? alpha(theme.palette.common.white, 0.1)
                  : alpha(theme.palette.common.black, 0.12)
              }`,
              boxShadow: isDark
                ? '0 24px 80px rgba(0, 0, 0, 0.55)'
                : '0 24px 70px rgba(23, 49, 69, 0.18)',
              overflow: 'hidden',
              '& .MuiLink-root': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              },
              '& .MuiButton-root': {
                borderRadius: 8,
                textTransform: 'none',
              },
              ...(isDark
                ? {
                    '& .MuiButton-contained.Mui-disabled': {
                      backgroundColor: alpha(theme.palette.common.white, 0.18),
                      color: alpha(theme.palette.common.white, 0.52),
                    },
                  }
                : {
                    '& .MuiButton-contained.Mui-disabled': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.12),
                      color: alpha(theme.palette.text.primary, 0.38),
                    },
                  }),
            };
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            padding: '8px 16px 0',
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            padding: '8px 16px 8px',
          }),
        },
      },
      // Row separators use the full divider colour; MUI's default fades it almost to nothing.
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.divider}` }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({ '--DataGrid-rowBorderColor': theme.palette.divider }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '8px 16px 12px',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(getTextFieldSizeSx(ownerState.size) ?? {}),
            '&.pa-textfield-size-tiny': tinyTextFieldSx,
            '&.pa-textfield-size-xs': xsTextFieldSx,
          }),
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            '&.pa-textfield-size-tiny': tinyTextFieldSx,
            '&.pa-textfield-size-xs': xsTextFieldSx,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: buttonBorderRadius,
            '& .MuiTouchRipple-root': {
              inset: 4,
              borderRadius: 8,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      // Segmented control: one outlined track, borderless options, the selected one a soft pill.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: toggleButtonBorderRadius,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
            padding: 2,
            gap: 2,
            // Animated groups (react-components/inputs/ToggleButtonGroup) draw the selection as
            // one sliding pill behind the options.
            '&.pa-toggle-animated .MuiToggleButton-root': {
              zIndex: 1,
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'transparent' },
            },
          }),
          grouped: {
            border: 0,
            borderRadius: toggleButtonBorderRadius - 3,
            margin: 0,
          },
          firstButton: {
            borderRadius: toggleButtonBorderRadius - 3,
          },
          middleButton: {
            borderRadius: toggleButtonBorderRadius - 3,
          },
          lastButton: {
            borderRadius: toggleButtonBorderRadius - 3,
            marginLeft: 0,
            borderLeft: 0,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const palette =
              ownerState.color && ownerState.color !== 'standard'
                ? theme.palette[ownerState.color]
                : theme.palette.primary;
            return {
              borderRadius: toggleButtonBorderRadius - 3,
              border: 0,
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected, &.Mui-selected:hover': {
                color: palette.main,
                backgroundColor: alpha(palette.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
              },
            };
          },
          sizeSmall: { padding: '3px 10px', fontSize: 12.5 },
          sizeMedium: { padding: '5px 12px', fontSize: 13 },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          icon: createElement(CheckboxUncheckedIcon),
          checkedIcon: createElement(CheckboxCheckedIcon),
          indeterminateIcon: createElement(CheckboxIndeterminateIcon),
        },
        styleOverrides: {
          root: ({ theme }) => ({
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              inset: 9,
              borderRadius: 4,
            },
            '& .MuiSvgIcon-root': {
              borderRadius: 17,
            },
            '&:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(
                theme.palette.action.active,
                theme.palette.action.hoverOpacity,
              ),
            },
            '&.MuiCheckbox-colorPrimary:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
            },
            '&.MuiCheckbox-colorSecondary:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(
                theme.palette.secondary.main,
                theme.palette.action.hoverOpacity,
              ),
            },
            '@media (hover: none)': {
              '&:hover .MuiSvgIcon-root': {
                backgroundColor: 'transparent',
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }),
          action: {
            alignSelf: 'center',
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            '&:last-child': {
              paddingBottom: theme.spacing(1.5),
            },
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: popoverBorderRadius,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: 25,
            '& .MuiChip-label': {
              paddingLeft: 8,
              paddingRight: 8,
            },
          },
        },
      },
    },
  });

export enum PlotColors {
  LIGHT_BLUE = 'light_blue',
  GOLD = 'gold',
  BLUE = 'blue',
}

export const plotColorsDark: Record<PlotColors, string> = {
  blue: '#004475',
  gold: '#FDBF0B',
  light_blue: '#3E96B5',
};

export const plotColorsLight: Record<PlotColors, string> = {
  blue: '#002641',
  gold: '#FDBF0B',
  light_blue: '#3E96B5',
};

export const salesforceColor = external.brand.salesforce;

export const getPlotColor = (theme: Theme, color: PlotColors) =>
  theme.palette.mode === 'dark' ? plotColorsDark[color] : plotColorsLight[color];

export type RgbString =
  | `rgb(${number},${number},${number})`
  | `rgb(${number}, ${number}, ${number})`;

export const getRgbaColor = (color: RgbString, opacity: number) =>
  color.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${opacity})`);

export const hexToRgba = (hex: `#${string}`, opacity: number) => {
  let r = 0;
  let g = 0;
  let b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = Number.parseInt(hex[1] + hex[1], 16);
    g = Number.parseInt(hex[2] + hex[2], 16);
    b = Number.parseInt(hex[3] + hex[3], 16);
  }
  // 6 digits
  else if (hex.length === 7) {
    r = Number.parseInt(hex[1] + hex[2], 16);
    g = Number.parseInt(hex[3] + hex[4], 16);
    b = Number.parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const overrideSx = (
  customSx: SystemStyleObject<Theme>,
  theme: Theme,
  userDefinedSx?: SxProps<Theme>,
): SystemStyleObject<Theme> => {
  if (!userDefinedSx) return customSx;
  if (Array.isArray(userDefinedSx)) {
    throw new Error('Array type is not supported for sx prop in overrideSx function');
  }

  // @ts-expect-error this works for our purposes
  return {
    ...customSx,
    ...(typeof userDefinedSx === 'function' ? userDefinedSx(theme) : userDefinedSx),
  };
};

const toolTag = (tool: keyof (typeof domain)['light']['tool']) => ({
  dark: domain.dark.tool[tool],
  light: domain.light.tool[tool],
});

/** Per-mode Domain colors for service tags (`InfoPill domain=`). */
export const serviceTagColors: Record<SimulationService, Record<ColorMode, DomainColor>> = {
  PVCosting: toolTag('pvcosting'),
  ESS: toolTag('ess'),
  PVSim: toolTag('pvsim'),
  PlantSim: toolTag('plantsim'),
  Project: toolTag('project'),
};

export const bodColor = '#ffc31d';
